<template>
  <div class="epc-toolbar" :class="`epc-toolbar--${variant}`">

    <!-- B9: status-bar (slot opcional). El consumidor provee el contenido con su propio styling. -->
    <div v-if="showStatusBar" class="epc-toolbar__status-bar">
      <slot name="status-bar" :context="context" />
    </div>

    <div class="epc-toolbar-content">
      <div
        v-for="group in groupedButtons"
        :id="group.id"
        :key="groupKey(group)"
        class="epc-control-group">

        <template v-for="btn in group.items">

          <!-- B4 + B5: button / toggle CON tooltip -->
          <b-tooltip
            v-if="isPlainButton(btn) && resolvedTooltip(btn)"
            :key="`${btn.id}:tt`"
            :label="resolvedTooltip(btn)"
            position="is-top">
            <div
              :id="`toolbar-${btn.id}`"
              class="epc-button inactive"
              :class="{ active: isActive(btn), disabled: isDisabled(btn) }"
              :aria-label="resolvedLabel(btn)"
              @click="onButtonClick(btn)">
              <b-icon :pack="resolvedIconPack(btn)" :icon="resolvedIcon(btn)" />
            </div>
          </b-tooltip>

          <!-- B4 + B5: button / toggle SIN tooltip -->
          <div
            v-else-if="isPlainButton(btn)"
            :id="`toolbar-${btn.id}`"
            :key="`${btn.id}:plain`"
            class="epc-button inactive"
            :class="{ active: isActive(btn), disabled: isDisabled(btn) }"
            :aria-label="resolvedLabel(btn)"
            @click="onButtonClick(btn)">
            <b-icon :pack="resolvedIconPack(btn)" :icon="resolvedIcon(btn)" />
          </div>

          <!-- B8: dropdown -->
          <SplitActionDropdown
            v-else-if="btn.type === 'dropdown' && isVisible(btn)"
            :button-id="`toolbar-${btn.id}`"
            :key="`${btn.id}:dd`"
            :options="resolvedDropdownOptions(btn)"
            :default-option="resolvedDefaultDropdownOption(btn)"
            :is-active="isActive(btn)"
            :main-icon="resolvedMainIcon(btn)"
            @option-selected="onDropdownSelected(btn, $event)"
          />

          <!-- B7: dynamic-group  → ya expandido en groupedButtons como items sintéticos -->
          <!-- B6: separator      → no renderiza nada; ya rompe el grupo en groupedButtons -->

        </template>
      </div>
    </div>
  </div>
</template>

<script>
import SplitActionDropdown from './components/buttons/SplitActionDropdown.vue'

const VALID_VARIANTS = ['inline', 'floating']
const VALID_TYPES = ['button', 'toggle', 'dropdown', 'dynamic-group', 'separator']

export default {
  name: 'Toolbar',

  components: {
    SplitActionDropdown,
  },

  props: {
    /**
     * Definición declarativa de botones.
     * Ver CONTRATO_TOOLBAR.md → Schema de botón (`ButtonSchema`).
     */
    buttons: {
      type: Array,
      default: () => [],
      validator(arr) {
        return arr.every(b => b && typeof b.id === 'string' && VALID_TYPES.includes(b.type))
      },
    },

    /**
     * Variante visual:
     *  - 'inline'   → en flujo normal (caso ViewerPageKonva).
     *  - 'floating' → position:absolute; bottom:10px (caso LayoutBuilder).
     */
    variant: {
      type: String,
      default: 'inline',
      validator: v => VALID_VARIANTS.includes(v),
    },

    /**
     * Contexto que se pasa como argumento a todas las funciones del schema
     * (visible, active, disabled, tooltipKey, payload, source, etc.).
     * El padre decide qué meter; el Toolbar nunca lee $store ni $refs.
     */
    context: {
      type: Object,
      default: () => ({}),
    },

    /** Si true, monta el slot #status-bar encima de la toolbar. */
    showStatusBar: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    /**
     * Agrupa botones consecutivos que comparten el mismo `group`.
     * Los items con type='separator' rompen el grupo en curso.
     * Items sin `group` quedan en un grupo anónimo (sin id).
     *
     * Los items con type='dynamic-group' se EXPANDEN aquí: por cada entry
     * devuelta por `source(ctx)` se inserta un item sintético basado en
     * `itemTemplate`, con sus funciones pre-bindeadas al item original.
     * De este modo el template de render no necesita conocer el caso
     * dinámico: cada synthetic item se comporta como un button/toggle normal.
     *
     * Cada entrada devuelta:
     *   { name: string|null, id: string|null, items: ButtonSchema[] }
     */
    groupedButtons() {
      const groups = []
      let current = null

      const flush = () => {
        if (current && current.items.length > 0) groups.push(current)
        current = null
      }

      const ensureGroup = (groupName) => {
        if (!current || current.name !== groupName) {
          flush()
          current = {
            name: groupName,
            id: groupName ? `toolbar-group-${groupName}` : null,
            items: [],
          }
        }
      }

      for (const btn of this.buttons) {
        if (btn.type === 'separator') {
          flush()
          continue
        }

        const groupName = btn.group ?? null

        if (btn.type === 'dynamic-group') {
          ensureGroup(groupName)
          const source = this._resolve(btn.source, this.context) || []
          const tpl = btn.itemTemplate || {}
          source.forEach((item, idx) => {
            const key = btn.itemKey ? btn.itemKey(item) : idx
            current.items.push(this._buildSyntheticItem(btn.id, key, tpl, item))
          })
          continue
        }

        ensureGroup(groupName)
        current.items.push(btn)
      }
      flush()

      return groups
    },
  },

  methods: {
    /**
     * Resuelve una propiedad del schema que puede ser:
     *  - un valor estático (string, boolean, number…)
     *  - una función (ctx, item?) => valor
     *
     * @param {*} value
     * @param  {...any} args  Se pasan a la función si `value` es función.
     * @return {*} valor resuelto, o el propio `value` si no es función.
     */
    _resolve(value, ...args) {
      return typeof value === 'function' ? value(...args) : value
    },

    /**
     * Bindea cada función del `itemTemplate` para que reciba (ctx, item)
     * en vez de solo (ctx). Los valores estáticos pasan tal cual.
     * El item resultante tiene la misma forma que un ButtonSchema normal.
     */
    _buildSyntheticItem(parentId, key, tpl, item) {
      const bind = (val) =>
        typeof val === 'function' ? (ctx) => val(ctx, item) : val
      return {
        ...tpl,
        id: `${parentId}-${key}`,
        icon:       bind(tpl.icon),
        iconPack:   bind(tpl.iconPack),
        tooltipKey: bind(tpl.tooltipKey),
        labelKey:   bind(tpl.labelKey),
        visible:    bind(tpl.visible),
        active:     bind(tpl.active),
        disabled:   bind(tpl.disabled),
        payload:    bind(tpl.payload),
      }
    },

    /** Key estable de Vue para cada grupo. */
    groupKey(group) {
      return group.name || `__anon_${group.items[0].id}`
    },

    /** ¿El botón debe renderizarse? Default true. */
    isVisible(btn) {
      const v = this._resolve(btn.visible, this.context)
      return v === undefined ? true : !!v
    },

    /** ¿Es un botón simple/toggle visible? Helper para v-if/v-else-if del template. */
    isPlainButton(btn) {
      return (btn.type === 'button' || btn.type === 'toggle') && this.isVisible(btn)
    },

    /** ¿El botón está activo? Solo significativo en 'toggle' y 'dropdown'. */
    isActive(btn) {
      return !!this._resolve(btn.active, this.context)
    },

    /** ¿El botón está deshabilitado? Default false. */
    isDisabled(btn) {
      return !!this._resolve(btn.disabled, this.context)
    },

    /** Icono resuelto. */
    resolvedIcon(btn) {
      return this._resolve(btn.icon, this.context)
    },

    /** Pack de iconos. Default 'fas'. */
    resolvedIconPack(btn) {
      return this._resolve(btn.iconPack, this.context) || 'fas'
    },

    /** Texto traducido del tooltip (o null si no hay tooltipKey). */
    resolvedTooltip(btn) {
      const key = this._resolve(btn.tooltipKey, this.context)
      return key ? this.$t(key) : null
    },

    /** Texto traducido para aria-label / accesibilidad. */
    resolvedLabel(btn) {
      const key = this._resolve(btn.labelKey, this.context)
      return key ? this.$t(key) : null
    },

    /** Icono principal del dropdown (resuelto si es función). */
    resolvedMainIcon(btn) {
      return this._resolve(btn.mainIcon, this.context)
    },

    /**
     * Mapea las options del schema (con `labelKey`) a las que espera
     * SplitActionDropdown (con `label` ya traducido).
     */
    resolvedDropdownOptions(btn) {
      // `options` puede ser estático o una función (ctx) => [...] para menús
      // dinámicos (ej. estados por capa activa). Se resuelve con el context.
      const options = this._resolve(btn.options, this.context) || []
      return options.map(opt => ({
        value: opt.value,
        label: opt.labelKey ? this.$t(opt.labelKey) : (opt.label ?? ''),
        icon: opt.icon,
        iconPack: opt.iconPack,
      }))
    },

    /**
     * Devuelve la option (ya traducida) que coincide con
     * `defaultOptionValue` para que SplitActionDropdown la use como inicial.
     */
    resolvedDefaultDropdownOption(btn) {
      if (!btn.defaultOptionValue) return null
      const resolved = this.resolvedDropdownOptions(btn)
      return resolved.find(o => o.value === btn.defaultOptionValue) || null
    },

    /**
     * B11 — Emit único `@action`.
     * Payload:
     *  - Si schema define `payload`, usa ese (resuelve función).
     *  - Si es 'toggle', envía el nuevo estado booleano.
     *  - Si no, null.
     */
    onButtonClick(btn) {
      if (this.isDisabled(btn)) return

      let payload
      if (btn.payload !== undefined) {
        payload = this._resolve(btn.payload, this.context)
      } else if (btn.type === 'toggle') {
        payload = !this.isActive(btn)
      } else {
        payload = null
      }

      this.$emit('action', {
        id: btn.id,
        handler: btn.handler,
        payload,
      })
    },

    /**
     * Handler del evento `option-selected` de SplitActionDropdown.
     * Reemite a través del @action genérico, con la option seleccionada como payload.
     */
    onDropdownSelected(btn, option) {
      if (this.isDisabled(btn)) return

      this.$emit('action', {
        id: btn.id,
        handler: btn.handler,
        payload: option,
      })
    },
  },
}
</script>

<style scoped>
.epc-toolbar {
  width: 100%;
}

/* ─── Variant: inline ───────────────────────────────────────────
   Comportamiento de footer: barra de estado opcional arriba +
   toolbar de botones abajo. Provee el chrome (fondo, borde, alto). */
.epc-toolbar--inline {
  background-color: #fff;
  border-top: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  height: 6rem; /* equivalente a h-24 (96px) */
  position: relative;
}

.epc-toolbar--inline .epc-toolbar__status-bar {
  flex-shrink: 0;
}

.epc-toolbar--inline .epc-toolbar-content {
  flex: 1 1 auto;
  padding: 0 1rem;
}

/* ─── Variant: floating ─────────────────────────────────────────
   Posiciona la toolbar absoluta sobre el viewer; pointer-events:none
   en el contenedor para que los clicks vacíos pasen al canvas. */
.epc-toolbar--floating {
  bottom: 10px;
  left: 10px;
  pointer-events: none;
  position: absolute;
  text-align: center;
  width: calc(100% - 20px);
  z-index: 5;
}

.epc-toolbar--floating .epc-toolbar-content {
  pointer-events: none;
}

/* ─── Contenido (lista de grupos) ───────────────────────────────
   Layout horizontal centrado, válido para los dos variants. */
.epc-toolbar-content {
  align-items: center;
  display: flex;
  justify-content: center;
  overflow: visible;
}

/* ─── Grupo de control (control-group) ──────────────────────────
   Cada wrapper de botones consecutivos. Pointer-events:all para
   recuperar la interactividad en floating. Usamos inline-flex para
   alinear correctamente los botones independientemente de si están
   o no envueltos en un <b-tooltip> (que añade spans intermedios). */
.epc-control-group {
  background-color: rgba(34, 34, 34, 0.94);
  border-radius: 5px;
  box-shadow: 1px 3px 10px 0 rgba(0, 0, 0, 0.5);
  color: #f4f4f4;
  display: inline-flex;
  align-items: center;
  margin: 0 5px;
  pointer-events: all;
}

/* Los wrappers que añade Buefy al b-tooltip no deben romper el alineado.
   Buefy aplica `flex-direction: column` para position="is-top" (apila el
   trigger y el contenido del tooltip verticalmente). Eso da más altura al
   .b-tooltip que a los botones hermanos sin tooltip. Forzamos row + reset
   de espacios implícitos para que todos los items midan igual. */
.epc-control-group :deep(.b-tooltip),
.epc-control-group :deep(.tooltip-trigger) {
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  margin: 0;
  padding: 0;
  line-height: 1;
}

/* ─── Botón individual ─────────────────────────────────────────── */
.epc-button {
  border: 1px solid rgba(34, 34, 34, 0);
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 4px;
  padding: 6px;
  position: relative;
}

.epc-button:hover,
.epc-button.active {
  border: 1px solid #00bfff;
  color: #00bfff;
}

.epc-button.active,
.epc-button:focus {
  border-radius: 3px;
  box-shadow: inset 0 2px 2px 0 #000;
  color: #00bfff;
  outline: none;
}

.epc-button.disabled {
  cursor: not-allowed;
  opacity: 0.5;
  pointer-events: none;
}
</style>
